import React, { useEffect } from 'react';
import { useLocation, useNavigate, Navigate, redirect } from 'react-router-dom';

import api from '../services/api';
import { getFingerprint, getDeviceName } from '../services/fingerprintService';
import LoadingComponent from './LoadingPage';


let AuthContext = React.createContext(null);

export function AuthProviderAdmin({ children }) {
  const [user, setUser] = React.useState();
  const [perms, setPerm] = React.useState([]);
  const [name, setName] = React.useState();
  const [logout, setLogout] = React.useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const location = useLocation();
  const TOKEN_KEY = 'tokenClient';

  // Estado para 2FA
  const [pending2FA, setPending2FA] = React.useState(null);

  React.useEffect(() => {
    if (!location.pathname.startsWith("/admin")){
      setLoading(false)
      return
    }
    if (validateToken()) {
      getUser();
    }else{
      setLoading(false)
    }
  }, []);


  function getToken() {
    const cookieValue = document.cookie
      .split(';')
      .map((row) => row.trim())
      .find((row) => row.startsWith(`${TOKEN_KEY}=`))
      ?.split('=')[1];
    return cookieValue;
  }

  function setToken(tk) {
    let now = new Date();
    const time = now.getTime();
    const expireTime = time + 1000 * 60 * 30 * 48;
    now.setTime(expireTime);
    document.cookie = `${TOKEN_KEY}=${tk}; expires=${now.toUTCString()}; SameSite=Strict `;
    api.defaults.headers.common['Authorization'] = tk;
  }

  function deleteCookies() {
    var allCookies = document.cookie.split(';');
    for (var i = 0; i < allCookies.length; i++){
      if(allCookies[i].includes(TOKEN_KEY)){
        document.cookie = allCookies[i] + '=;expires=' + new Date(0).toUTCString();
      }
    }
  }

  function validateToken() {
    if (getToken() == undefined || getToken() == "") {
      setUser(null);
      return false;

    }
    setToken(getToken());
    return true;
  }

  function getUser(){
    setLoading(true)
    api
      .get('/admin/auth/me/')
      .then((res) => {
        setUser(res.data.username);
        setName(res.data.name);
        setPerm(res.data.permissions);
      })
      .catch((err) => {
        deleteCookies();
        setUser(null);
        setPerm([]);
        setName(null);

      }).finally(()=>{
        setLoading(false)
      })
  }

  const checkPerm = (p) => perms.filter((perm) => perm.permission === p);
  const hasPerm = (p) => checkPerm(p).length > 0;

  /**
   * Login com suporte a 2FA
   * @param {Object} cred - Credenciais {username, password}
   * @param {Function} callback - Callback de sucesso
   * @param {Function} clerr - Callback de erro
   * @param {Function} on2FA - Callback quando 2FA é necessário
   */
  const signin = async (cred, callback, clerr, on2FA) => {
    try {
      // Obter fingerprint do dispositivo
      const fingerprint = await getFingerprint();

      const response = await api.post('/admin/auth/token/', {
        ...cred,
        fingerprint
      });

      const data = response.data;

      // Verificar se requer 2FA
      if (data.requires_2fa) {
        // Armazenar dados pendentes
        setPending2FA({
          pendingToken: data.pending_token,
          maskedEmail: data.masked_email,
          fingerprint
        });

        // Chamar callback de 2FA se fornecido
        if (on2FA) {
          on2FA(data);
        }

        // Pequeno delay para mostrar mensagem de loading antes de navegar
        setTimeout(() => {
          navigate('/admin/2fa');
        }, 800);
        return;
      }

      // Login direto (dispositivo confiável)
      const token = `Bearer ${data.access_token}`;
      setToken(token);
      getUser();
      callback();

    } catch (err) {
      clerr(err?.response?.data?.detail || 'Credenciais invalidas');
    }
  };

  /**
   * Verifica código 2FA
   * @param {string} code - Código de 6 dígitos
   * @param {boolean} trustDevice - Se deve confiar no dispositivo
   * @param {string} deviceName - Nome do dispositivo (opcional)
   * @returns {Promise<Object>} Resultado da verificação
   */
  const verify2FA = async (code, trustDevice = false, deviceName = null) => {
    if (!pending2FA) {
      throw new Error('Nenhuma sessão de 2FA pendente');
    }

    try {
      const response = await api.post('/admin/auth/2fa/verify/', {
        pending_token: pending2FA.pendingToken,
        code,
        trust_device: trustDevice,
        device_name: deviceName || getDeviceName()
      });

      const data = response.data;

      if (data.success) {
        // Login bem-sucedido
        const token = `Bearer ${data.access_token}`;
        setToken(token);
        setPending2FA(null);
        getUser();

        return {
          success: true,
          message: data.message
        };
      }

      return {
        success: false,
        error: data.error,
        message: data.message,
        attemptsRemaining: data.attempts_remaining
      };

    } catch (err) {
      return {
        success: false,
        error: 'request_failed',
        message: err?.response?.data?.detail || 'Erro ao verificar código'
      };
    }
  };

  /**
   * Reenvia código 2FA por email
   * @returns {Promise<Object>} Resultado do reenvio
   */
  const resend2FACode = async () => {
    if (!pending2FA) {
      throw new Error('Nenhuma sessão de 2FA pendente');
    }

    try {
      const response = await api.post('/admin/auth/2fa/resend/', {
        pending_token: pending2FA.pendingToken
      });

      const data = response.data;

      return {
        success: data.success,
        message: data.message,
        waitSeconds: data.wait_seconds,
        resendsRemaining: data.resends_remaining
      };

    } catch (err) {
      return {
        success: false,
        message: err?.response?.data?.detail || 'Erro ao reenviar código'
      };
    }
  };

  /**
   * Cancela verificação 2FA e volta para login
   */
  const cancel2FA = () => {
    setPending2FA(null);
    navigate('/admin/login');
  };

  const signout = async (callback = () => {}) => {
    try {
      await api.post('/admin/auth/logout/');
    } catch (error) {
      console.error('Admin logout API call failed:', error);
    }

    setUser(null);
    setPending2FA(null);
    deleteCookies();
    navigate('/admin/login');
    callback();
  };

  let value = {
    user,
    name,
    validateToken,
    signin,
    signout,
    hasPerm,
    getToken,
    // 2FA methods
    pending2FA,
    verify2FA,
    resend2FACode,
    cancel2FA
  };

  return <AuthContext.Provider value={value}>{
    loading ?   <LoadingComponent loading={loading} />  : children
  }</AuthContext.Provider>;
}

export function RequireAuthAdmin({ children }) {
  let auth = useAuth();
  let location = useLocation();
  if (auth.user === undefined) {
    return null;
  }

  if (!auth.user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

export function AuthStatus() {
  let auth = useAuth();
  let navigate = useNavigate();

  if (!auth.user) {
    return <p>You are not logged in.</p>;
  }

  return (
    <p>
      Welcome {auth.user}!{' '}
      <button
        onClick={() => {
          auth.signout(() => navigate('/admin/'));
        }}
      >
        Sign out
      </button>
    </p>
  );
}
